import { Dispatch, FC, Fragment, SetStateAction } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { omit } from 'lodash';

import { Select, TextField, TextFieldHint } from '@components/base';
import { LinkCondition, LinkParams } from '@models/MenuItem';
import { BehaviorScope, SourceBehaviors } from '@models/PageSection';
import ProviderSelector from '@modules/layouts/components/ProviderSelector';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Params = LinkParams | Record<string, any>;

type LinkItemFieldsProps = {
  disabled?: boolean;
  params?: Params;
  setParams: Dispatch<SetStateAction<Params>>;
};

const PartialLinkParamsFieldNames = ['behavior', 'condition'] satisfies Array<keyof LinkParams>;

type PartialLinkParamsFieldName = (typeof PartialLinkParamsFieldNames)[number];

export const LinkParamsFieldNames = [
  'url',
  'providerIdentifier',
  'linkTitle',
  ...PartialLinkParamsFieldNames,
] satisfies Array<keyof LinkParams>;

export const LinkItemFields: FC<LinkItemFieldsProps> = ({
  disabled,
  setParams,
  params = {
    url: '',
  },
}) => {
  const { t } = useTranslation();

  const updateValue = (field: PartialLinkParamsFieldName, value: string) =>
    setParams(prev => (value ? { ...prev, [field]: value } : omit(prev, field)));

  return (
    <Fragment>
      <TextField
        disabled={disabled}
        fullWidth
        label={t('url.label')}
        name='link-url'
        onChange={({ currentTarget: { value } }) => setParams(prev => ({ ...prev, url: value }))}
        placeholder='https://example.com'
        required
        type='url'
        value={params?.url ?? ''}
      />

      <TextField
        disabled={disabled}
        fullWidth
        label={t('linkTitle')}
        name='link-title'
        onChange={({ currentTarget: { value } }) =>
          setParams(prev => ({ ...prev, linkTitle: value }))
        }
        required
        slotProps={{
          input: {
            endAdornment: (
              <TextFieldHint
                hint={<Trans components={[<em />]} i18nKey='linkTitle.description' />}
                position='end'
              />
            ),
          },
        }}
        value={params?.linkTitle ?? ''}
      />

      <Select
        defaultOption={{ disabled: false, label: t('none') }}
        label={t('behavior')}
        onChange={({ target: { value } }) => updateValue('behavior', value)}
        options={SourceBehaviors[BehaviorScope.LINK].map(behavior => ({
          label: behavior,
          value: behavior,
        }))}
        value={params?.behavior}
      />

      <Select
        defaultOption={{ disabled: false, label: t('none') }}
        label={t('condition')}
        onChange={({ target: { value } }) => updateValue('condition', value)}
        options={Object.values(LinkCondition).map(condition => ({
          label: condition,
          value: condition,
        }))}
        value={params?.condition}
      />

      <ProviderSelector
        label={t('provider')}
        onChange={providerIdentifier =>
          setParams(prev =>
            providerIdentifier ? { ...prev, providerIdentifier } : delete prev.providerIdentifier
          )
        }
        value={params.providerIdentifier}
      />
    </Fragment>
  );
};
